import React, { ReactNode, useState } from 'react';
import { HStack, VStack, ScrollView, Actionsheet, Skeleton, Box } from 'native-base';
import { useGiftCardStyles } from '~/shared/styles';
import { H3, Caption } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faMap } from '@fortawesome/free-solid-svg-icons';
import OurPressable from '~/shared/Pressable';
import { GiftCard, GiftCardLocationResponse, useAppSettings, useI18n } from '@springbig/shared';
import Image, { LogoMd } from '~/shared/Image';

type CardStyledContainerProps = {
    children: ReactNode;
    isFlipped?: boolean;
};

const CardStyledContainer = ({
    children,
    isFlipped
}: CardStyledContainerProps) => {

    const {
        card_color,
        card_color_muted
    } = useGiftCardStyles();

    return <HStack
        backgroundColor={isFlipped ? card_color_muted : card_color}
        borderRadius={8}
        justifyContent={'space-between'}
        alignSelf={'center'}
        width={'100%'}
        height={170}
        p={4}
        mt={5}
    >
        { children }
    </HStack>;
};

const Card = ({
    card,
    locations,
}: {
    card?: GiftCard,
    locations?: GiftCardLocationResponse[],
}) => {

    const [isFlipped, setIsFlipped] = useState<boolean>(false);
    const { customIcons } = useAppSettings();
    const { t } = useI18n();

    const {
        card_text_color,
        icon,
    } = useGiftCardStyles();

    return (
        <CardStyledContainer isFlipped={isFlipped}>
            <OurPressable onPress={() => setIsFlipped(!isFlipped)} flex={1}>
                { !isFlipped ?
                    <VStack justifyContent={'space-between'} h={'100%'}>
                        <HStack space={2}>
                            <SpIcon icon={faMap} size={24} color={icon} />
                            <H3 color={card_text_color}>
                                { t('giftCard.locations') }
                            </H3>
                        </HStack>
                        <Caption color={card_text_color}>
                            { card.is_closed_loop ? t('giftCard.non_reloadable') : t('giftCard.reloadable') }
                        </Caption>
                    </VStack>
                    :
                    <Actionsheet isOpen={isFlipped} onClose={() => setIsFlipped(false)}>
                        <Actionsheet.Content zIndex={9999} overflow='auto' w={'clamp(320px, 100vw, 480px)'} alignSelf='center'>
                            <ScrollView w={'100%'} px={4}>
                                <VStack space={1} flex={1}>
                                    <Caption>
                                        { t('giftCard.used_at') }
                                    </Caption>
                                    <VStack flexWrap={'wrap'} px={4}>
                                        { locations && locations.map((location) =>

                                            <Caption
                                                fontSize={14}
                                                key={location.display_name}
                                                numberOfLines={1}
                                                textOverflow={'ellipsis'}
                                                maxWidth={'100%'}
                                            >
                                                { location.display_name }
                                            </Caption>
                                        )}
                                    </VStack>
                                </VStack>
                            </ScrollView>
                        </Actionsheet.Content>
                    </Actionsheet>
                }
            </OurPressable>
            <VStack justifyContent={'space-between'} alignItems={'flex-end'}>
                { customIcons && customIcons.GiftCardIcon ?
                    <Image uri={customIcons.GiftCardIcon} h={35} w={35} />
                    :
                    <LogoMd resizeMode="contain" h={35} />
                }
                <H3 fontSize={20} color={card_text_color}>
                    { card.balance }
                </H3>
            </VStack>
        </CardStyledContainer>
    );
};

export const SkeletonCard = () => {

    return (
        <Box mx={5} alignItems={'center'}>
            <CardStyledContainer>
                <VStack w='100%' justifyContent={'space-between'} >
                    <HStack alignItems={'center'} justifyContent={'space-between'}>
                        <HStack w='30%'>
                            <Skeleton h={5} w='20%' rounded={'lg'} mr={1} />
                            <Skeleton h={5} w='70%' rounded={'lg'} />
                        </HStack>
                        <Skeleton h={10} w='15%' rounded={'lg'} />
                    </HStack>
                    <Skeleton h={7} w={'6rem'} alignSelf={'flex-end'} rounded={'full'} />
                </VStack>
            </CardStyledContainer>
            <Skeleton h={4} w={'7rem'} rounded={'md'} borderRadius={5} my={2} alignSelf={'start'} />
            <Skeleton h={'2.2rem'} w={'8rem'} rounded={'lg'} mt={2} />
            <Skeleton h={5} w={'14rem'} rounded={'full'} borderRadius={5} my={4} />
            <Skeleton h={4} w={'6rem'} rounded={'full'} borderRadius={5} mb={3}/>
        </Box>
    );
};

export default Card;
